import Dexie, { Table } from 'dexie';
import { ClearDataBase } from './interfaces/clearDataBase';
import { AttachmentFile, AttachmentItemData, QCAttachment } from '90.quickConnect.Models/models';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { DocumentTransferState, TransferMode } from '90.quickConnect.Models/enums';
import { mapQCAttachmentFromAttachmentItemData } from '90.quickConnect.Models/mappings';
import { errorHandler } from '80.quickConnect.Core/helpers';

export class AttachmentDb extends Dexie implements ClearDataBase {
  // Tag
  private static readonly TAG = '40.quickConnect.DataAccess/indexedDb/dbs/attachmentDb.ts';

  attachments!: Table<QCAttachment>;

  private attachmentFiles!: Table<AttachmentFile, string>;

  logger: CustomLogger = CustomLogger.getInstance();

  // ATTENTION: Toute modification du schema doit être suivi d'une montée de version de la base de données afin de ne pas créer des crashs!! NE PAS CHANGER LES CLES PRIMAIRES DIRECTEMENT
  constructor() {
    super('attachmentDb');
    this.version(2).stores({
      attachments:
        'id,declarationId,distantUri,transferMode,[transferState+transferMode],[distantUri+transferMode+transferState]',
    });
    this.version(3).stores({ attachmentFiles: 'id' });
  }

  /**
   * Joint son fichier à une pièce jointe (lignes antérieures à attachmentFiles : fichier déjà présent)
   */
  private withFile = async (attachment: QCAttachment | undefined): Promise<QCAttachment | undefined> => {
    if (!attachment || attachment.file) return attachment;

    const stored = await this.attachmentFiles.get(attachment.id);
    if (!stored) {
      this.logger.warn(
        AttachmentDb.TAG,
        `ATTACHMENT_FILE_MISSING - id: ${attachment.id} - declarationId: ${attachment.declarationId} - state: ${attachment.transferState} - uri: ${attachment.distantUri}`,
      );
      return attachment;
    }

    const { data, name, type, lastModified } = stored;
    return { ...attachment, file: new File([data], name, { type, lastModified }) };
  };

  /**
   * Supprime des pièces jointes et leurs fichiers
   */
  private deleteByIds = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;

    await this.transaction('rw', this.attachments, this.attachmentFiles, async () => {
      await this.attachments.bulkDelete(ids);
      await this.attachmentFiles.bulkDelete(ids);
    });
  };

  async clearAllTables(logger: CustomLogger) {
    try {
      await this.transaction('rw', this.attachments, this.attachmentFiles, async () => {
        await this.attachments.clear();
        await this.attachmentFiles.clear();
      });
      logger.log(AttachmentDb.TAG, `all tables from ${this.name} have been cleared`);
    } catch (error) {
      errorHandler(AttachmentDb.TAG, error, 'clearAllTables');
    }
  }

  countRecords = async (): Promise<number> => {
    try {
      return await this.attachments.count();
    } catch (error: unknown) {
      errorHandler(AttachmentDb.TAG, error, 'countRecords');

      return 0;
    }
  };

  getAttachmentItemDataByDeclarationId = async (declarationId: string): Promise<QCAttachment[]> => {
    try {
      const records = this.attachments.where({ declarationId: declarationId });

      return records ? await records.toArray() : [];
    } catch (error: unknown) {
      errorHandler(
        AttachmentDb.TAG,
        error,
        `getAttachmentItemDataByDeclarationId (declarationId: ${declarationId})`,
        'warn',
      );
      return [];
    }
  };

  /**
   *
   * @returns la liste des attachment dont l'indexation n'a pas été faite
   * et dont la distantUri est vide
   * et dont le transferMode est Upload
   */
  public getAttachmentToIndex = async (): Promise<QCAttachment[]> => {
    const attachToIndex = this.attachments.where({
      distantUri: '',
      transferMode: TransferMode.UPLOAD,
      transferState: DocumentTransferState.ToBeTransfered,
    });

    return attachToIndex ? attachToIndex.toArray() : [];
  };

  /**
   * @returns la liste des attachment dont l'indexation a été faite
   * et dont la distantUri n'est pas vide
   * et dont le transferMode est Upload
   * et dont le fichier est présent en local (il est lu au moment de l'envoi, cf. getAttachmentById)
   */
  public getAttachmentToSend = async (): Promise<QCAttachment[]> => {
    const candidates = await this.attachments
      .where({
        transferState: DocumentTransferState.ToBeTransfered,
        transferMode: TransferMode.UPLOAD,
      })
      .and((attach) => !!attach.distantUri && attach.nbTry > 0)
      .toArray();

    // Clés seules : vérifie la présence des fichiers sans charger leurs octets
    const storedFileIds = new Set(
      await this.attachmentFiles
        .where('id')
        .anyOf(candidates.map(({ id }) => id))
        .primaryKeys(),
    );

    const hasFile = (attach: QCAttachment) => attach.file instanceof File || storedFileIds.has(attach.id);
    const toSend = candidates.filter(hasFile);
    const withoutFile = candidates.filter((attach) => !hasFile(attach));
    if (withoutFile.length > 0)
      this.logger.warn(
        AttachmentDb.TAG,
        `ATTACHMENT_SEND_FILE_MISSING - ${withoutFile
          .map(({ id, declarationId, distantUri }) => `${id} [dcl: ${declarationId} - uri: ${distantUri}]`)
          .join(' | ')}`,
      );

    return toSend;
  };

  public async updateAttachment(attach: QCAttachment): Promise<boolean> {
    let countRecordsUpdated = 0;
    return this.transaction('rw', this.attachments, async () => {
      // Update partiel : on ne ré-écrit JAMAIS le blob `file` ici, sinon on
      // déclenche un re-structured-clone qui peut planter sur Safari si le
      // handle d'origine est mort.
      const { file: omittedFile, ...changes } = attach;
      countRecordsUpdated = await this.attachments.update(attach.id, changes);
      return countRecordsUpdated > 0;
    });
  }

  public async updateAttachments(attachmentsToUpdate: QCAttachment[]): Promise<boolean> {
    try {
      // IMPORTANT (Safari/iPadOS) : on n'utilise PAS bulkPut ici car cela
      // re-sérialise tout l'enregistrement, File inclus. Sur WebKit un File
      // dont le handle a été invalidé fait échouer le structured clone
      // (DataCloneError) silencieusement pour CETTE ligne, sans abort de la
      // transaction : les autres lignes passent, celles-là sont perdues.
      // On fait donc des updates partiels qui ne touchent QUE les champs
      // modifiés par l'indexation. Le blob File n'est jamais ré-écrit.
      await this.transaction('rw', this.attachments, async () => {
        for (const attach of attachmentsToUpdate) {
          const { file: omittedFile, ...changes } = attach;
          const updated = await this.attachments.update(attach.id, changes);
          if (updated === 0) {
            this.logger.warn(
              AttachmentDb.TAG,
              `ATTACHMENT_INDEX_PERSIST_MISSING - id: ${attach.id} - declarationId: ${attach.declarationId}`,
            );
            continue;
          }

          const persistedAttachment = await this.attachments.get(attach.id);
          if (persistedAttachment?.distantUri !== attach.distantUri) {
            this.logger.warn(
              AttachmentDb.TAG,
              `ATTACHMENT_INDEX_PERSIST_FAILED - id: ${attach.id} - declarationId: ${attach.declarationId} - uri: ${attach.distantUri} - uri persisted: false`,
            );
          }
        }
      });
      return true;
    } catch (error) {
      errorHandler(
        AttachmentDb.TAG,
        error,
        `updateAttachments (ids: ${attachmentsToUpdate.map(({ id }) => id).join(', ')})`,
        'warn',
      );
      return false;
    }
  }

  /**
   * supprime les attachments à purger
   */
  public removeAttachmentToPurge = async (): Promise<void> => {
    const logger = CustomLogger.getInstance();
    try {
      const today = new Date();
      const attachToDelete = this.attachments
        .where('transferState')
        .notEqual('1')
        .and((attach) => {
          return attach.expirationDate != undefined && attach.expirationDate < today;
        });
      const toDelete = await attachToDelete.toArray();
      if (toDelete.length > 0)
        logger.warn(
          AttachmentDb.TAG,
          `ATTACHMENT_PURGED - ${toDelete.map(({ id, declarationId }) => `${id} [dcl: ${declarationId}]`).join(' | ')}`,
        );

      await this.deleteByIds(toDelete.map(({ id }) => id));
    } catch (error) {
      errorHandler(AttachmentDb.TAG, error, 'removeAttachmentToPurge');
    }
  };

  /**
   * Update de declarationId pour les attachments
   * @param attachmentsFile liste des attachments à mettre à jour
   * @param declarationId  id de la déclaration
   */
  public updateDeclIdForAttachment = async (
    attachmentsFile: AttachmentItemData[],
    declarationId: string,
    sendAPI: boolean,
  ): Promise<void> => {
    try {
      await Promise.all(
        attachmentsFile.map(async (attach) => {
          const updated = await this.attachments.update(attach.id, {
            declarationId,
            ...(sendAPI && {
              transferState: DocumentTransferState.ToBeTransfered,
            }),
          });

          if (updated === 0) {
            this.logger.warn(
              AttachmentDb.TAG,
              `ATTACHMENT_TRANSFER_STATE_MISSING - id: ${attach.id} - declarationId: ${declarationId}`,
            );
          }
        }),
      );
    } catch (error) {
      errorHandler(
        AttachmentDb.TAG,
        error,
        `updateDclIdForAttachment (declarationId: ${declarationId} - ids: ${attachmentsFile
          .map(({ id }) => id)
          .join(', ')})`,
        'warn',
      );
    }
  };

  public async updateAttachmentItemDataForWorkFlow(
    attachmentsFile: AttachmentItemData[],
    declarationId: string,
    sendAPI: boolean,
    attachItemDataIds: string[],
  ): Promise<void> {
    if (attachItemDataIds.length === 0) return;

    return this.transaction('rw', this.attachments, async () => {
      const attachItemDataCurrentStep = attachmentsFile.filter(({ id }: AttachmentItemData) =>
        attachItemDataIds.includes(id),
      );

      for (const attach of attachItemDataCurrentStep) {
        const updated = await this.attachments.update(attach.id, {
          declarationId,
          ...(sendAPI && {
            transferState: DocumentTransferState.ToBeTransfered,
          }),
        });

        if (updated === 0) {
          this.logger.warn(
            AttachmentDb.TAG,
            `ATTACHMENT_TRANSFER_STATE_MISSING - id: ${attach.id} - declarationId: ${declarationId}`,
          );
        }
      }
    });
  }

  /**
   * Ajout d'un attachment avec son fichier en BDD
   * @param attachment attachment à ajouter
   * @param file  fichier à ajouter dans l'attachment
   * @param update Optionnel - Remplace la ligne existante (réouverture d'une déclaration). Un fichier déjà
   * stocké dans attachmentFiles pour cet id est conservé : seules les métadonnées sont réécrites
   */
  public addAttachmentItem = async (
    attachment: AttachmentItemData,
    file: File,
    declarationId: string,
    update?: boolean,
  ): Promise<void> => {
    try {
      const qcAttach = mapQCAttachmentFromAttachmentItemData(attachment, declarationId);

      // Clé seule : les lignes antérieures à attachmentFiles n'y ont pas de fichier, il y est donc écrit
      const fileStored = !!update && (await this.attachmentFiles.where('id').equals(qcAttach.id).count()) > 0;

      // Lecture des octets avant la transaction (IndexedDB la fermerait pendant cet await), tant que le
      // fichier temporaire de WebKit est encore disponible
      const attachmentFile: AttachmentFile | undefined = fileStored
        ? undefined
        : {
            id: qcAttach.id,
            data: await file.arrayBuffer(),
            name: file.name,
            type: file.type,
            lastModified: file.lastModified,
          };

      // Métadonnées et fichier sont écrits ensemble ou pas du tout
      await this.transaction('rw', this.attachments, this.attachmentFiles, async () => {
        if (update) {
          await this.attachments.put(qcAttach, qcAttach.id);
        } else {
          await this.attachments.add(qcAttach);
        }
        if (attachmentFile) await this.attachmentFiles.put(attachmentFile);
      });
    } catch (error) {
      this.logger.warn(
        AttachmentDb.TAG,
        `ATTACHMENT_ADD_FAILED - id: ${attachment.id} - declarationId: ${declarationId} - update: ${!!update} - name: ${
          file?.name
        } - size: ${file?.size} - type: ${file?.type} - error: ${
          error instanceof Error ? `${error.name}: ${error.message}` : String(error)
        }`,
      );
    }
  };

  /**
   * Pièce jointe avec son fichier
   *
   * @param {string} attachmentId
   * @memberof AttachmentDb
   */
  public getAttachmentById = async (attachmentId: string): Promise<QCAttachment | undefined> => {
    try {
      return await this.withFile(await this.attachments.get(attachmentId));
    } catch (error: unknown) {
      errorHandler(AttachmentDb.TAG, error, `getAttachmentById (id: ${attachmentId})`, 'warn');
    }
  };

  /**
   * Métadonnées d'une pièce jointe, sans charger les octets de son fichier
   * (seules les lignes antérieures à attachmentFiles portent encore leur fichier dans `file`)
   */
  public getAttachmentMetadataById = async (attachmentId: string): Promise<QCAttachment | undefined> => {
    try {
      return await this.attachments.get(attachmentId);
    } catch (error: unknown) {
      errorHandler(AttachmentDb.TAG, error, `getAttachmentMetadataById (id: ${attachmentId})`, 'warn');
    }
  };

  /**
   *
   *
   * @param {AttachmentItemData} attachment
   * @memberof AttachmentDb
   */
  public deleteFromId = async (attachment: AttachmentItemData): Promise<void> => {
    try {
      await this.deleteByIds([attachment.id]);
    } catch (error) {
      errorHandler(AttachmentDb.TAG, error, `deleteFromId (id: ${attachment.id} - name: ${attachment.fileName})`);
    }
  };

  deleteAttachmentItemsDataByDeclarationId = async (declarationId: string): Promise<number> => {
    try {
      const ids = (await this.attachments.where({ declarationId: declarationId }).primaryKeys()) as string[];
      await this.deleteByIds(ids);
      return ids.length;
    } catch (error: unknown) {
      errorHandler(
        AttachmentDb.TAG,
        error,
        `deleteAttachmentItemsDataByDeclarationId (declarationId: ${declarationId})`,
      );
    }
    return 0;
  };

  /**
   * Pièce jointe avec son fichier
   */
  public getFileByDistantUri = async (distantUri: string): Promise<QCAttachment | undefined> => {
    try {
      const doc = await this.attachments
        .where({
          distantUri: distantUri,
        })
        .first();

      return await this.withFile(doc);
    } catch (error) {
      errorHandler(AttachmentDb.TAG, error, `getFileByDistantUri (uri: ${distantUri})`);
    }
  };
}
const attachmentDb = new AttachmentDb();

export default attachmentDb;
